import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from 'hooks/useAuth';
import { Col, Row, Card, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SingleNote from 'components/SingleNote';
import companyServices from 'services/company.services';
import AsyncSelect from 'components/common/search/AsyncSelect';
import useFetchCompany from 'pages/Company/services/useFetchCompany';
import { ROLES } from 'constants/index';
import AuthenticationWrapper from 'components/authentication/AuthenticationWrapper';
import { useCompanyContext } from 'pages/Company/useCompanyContext';

export default function SupervisorAndNotes() {
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { data, loadingDetail, companyId } = useCompanyContext();
  const { viewAsRole } = useAuth();
  const { companySupervisor, fetchCompanySupervisor } = useFetchCompany();
  const [dataUser, setDataUser] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const isAdmin = viewAsRole === ROLES.ADMIN;

  const onUpdateNote = async formValue => {
    try {
      await companyServices.updateCompany(companyId, { ...data, ...formValue });
    } catch (err) {
      console.error(err);
    }
  };

  const onSelectItem = async item => {
    try {
      const newUser = { id: item.value, name: item.label, email: item?.email };
      await companyServices.assignToComSupervisor(companyId, item.value);
      setDataUser(dataUser.filter(record => record.id !== item.value));
      setSelectedUser([...selectedUser, newUser]);
      toast.success('Assign supervisors successfully!', {
        theme: 'colored'
      });
    } catch (err) {
      console.error(err.message);
      toast.error('Assign supervisors failed!', {
        theme: 'colored'
      });
    }
  };

  const onDeleteSuperVisor = async supervisorId => {
    try {
      if (loadingDelete) return;
      setLoadingDelete(true);
      await companyServices.deleteCompanySupervisor(companyId, supervisorId);
      toast.success('The supervisor was successfully deleted!');
      await fetchCompanySupervisor(companyId);
    } catch (err) {
      toast.error(err?.error?.message || 'Could not delete the supervisor.');
    } finally {
      setLoadingDelete(false);
    }
  };

  useEffect(() => {
    setSelectedUser(companySupervisor);
  }, [companySupervisor]);

  useEffect(() => {
    if (companyId) {
      fetchCompanySupervisor(companyId);
    }
  }, [companyId]);

  const fetchAvailableSupervisor = inputValue => {
    return companyServices.getListAvailableToSupervisor(companyId, {
      user_name: inputValue
    });
  };

  return (
    <Row className="g-3 supervisor-note">
      <Col xs={12} lg={6} className="d-flex">
        <Card className="mb-3 align-self-stretch flex-1 single-note">
          <>
            <Card.Header className="d-flex justify-content-between">
              <h5 className="fs-0 d-inline-flex align-items-center mb-0">Supervisor</h5>
              <AuthenticationWrapper
                viewAsRole={viewAsRole}
                permissions={[ROLES.ADMIN, ROLES.CABFARE_INTERNAL]}
              >
                <AsyncSelect
                  value={null}
                  placeholder="Search..."
                  fetchFn={fetchAvailableSupervisor}
                  onChange={onSelectItem}
                  wrapperClassname="ms-2"
                  listFilteredItem={selectedUser.map(user => user.id)}
                />
              </AuthenticationWrapper>
            </Card.Header>
            <Card.Body className="border-top p-0">
              <Table responsive size="sm" className="fs--1 table-responsive visible-scrollbar">
                <thead>
                  <tr>
                    <th className="text-900">Name</th>
                    <th className="text-900">Email</th>
                    <th className="text-900 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUser.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td className="text-center">
                        {isAdmin && (
                          <FontAwesomeIcon
                            icon="trash"
                            className={classNames('ms-1 fs--1', {
                              'opacity-25': loadingDelete,
                              'cursor-pointer': !loadingDelete
                            })}
                            onClick={() => onDeleteSuperVisor(item.id)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <SingleNote
          name="notes"
          titleClass="fs-0"
          title="Company Note"
          value={data?.notes}
          loadingInformation={loadingDetail}
          handleUpdate={onUpdateNote}
          hasPermission={[ROLES.ADMIN, ROLES.CABFARE_INTERNAL, ROLES.GROUP].includes(viewAsRole)}
        />
      </Col>
    </Row>
  );
}
